import type { CatalogSnapshot, Publication, Subject } from '@lastrouter/contracts';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  Field,
  Input,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@lastrouter/ui';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { errorMessage, useApiMutation } from '../../api/hooks.js';
import { EmptyState, StatusBadge } from '../../components/common.js';
import { formatDateTime } from '../../lib/format.js';

function SubjectCard({ s }: { s: Subject }) {
  const { t } = useTranslation();
  const dimName = (id: string) => s.dimensoes.find((d) => d.id === id)?.nome ?? id;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{s.nome}</CardTitle>
        {s.descricao ? <CardDescription>{s.descricao}</CardDescription> : null}
        <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-[length:var(--lr-text-sm)] text-fg-secondary">
          <span>
            {t('context.source')}: <span className="text-fg">{s.fonte}</span>
          </span>
          <span>
            {t('context.app')}: <span className="font-mono text-fg">{s.app}</span>
          </span>
          <span>
            {t('context.business')}: <span className="text-fg">{s.donos.negocio}</span>
          </span>
          <span>
            {t('context.technical')}: <span className="text-fg">{s.donos.tecnico}</span>
          </span>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        <section aria-labelledby={`${s.id}-metricas`}>
          <h3 id={`${s.id}-metricas`} className="mb-2 font-semibold">
            {t('context.metrics')}
          </h3>
          <Table>
            <TableCaption>{t('context.metrics')}</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>{t('context.form.name')}</TableHead>
                <TableHead>{t('context.unit')}</TableHead>
                <TableHead>{t('context.expression')}</TableHead>
                <TableHead>{t('context.synonyms')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {s.metricas.map((m) => (
                <TableRow key={m.id}>
                  <TableCell>
                    <span className="font-medium">{m.nome}</span> <Badge>{t('context.version', { v: m.versao })}</Badge>
                    <p className="text-[length:var(--lr-text-xs)] text-fg-secondary">{m.descricao}</p>
                  </TableCell>
                  <TableCell>{m.unidade}</TableCell>
                  <TableCell className="font-mono text-[length:var(--lr-text-sm)]">{m.expressao}</TableCell>
                  <TableCell>{m.sinonimos.join(', ') || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </section>

        <section aria-labelledby={`${s.id}-dimensoes`}>
          <h3 id={`${s.id}-dimensoes`} className="mb-2 font-semibold">
            {t('context.dimensions')}
          </h3>
          <Table>
            <TableCaption>{t('context.dimensions')}</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>{t('context.form.name')}</TableHead>
                <TableHead>{t('context.field')}</TableHead>
                <TableHead>{t('context.type')}</TableHead>
                <TableHead>{t('context.synonyms')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {s.dimensoes.map((d) => (
                <TableRow key={d.id}>
                  <TableCell>
                    <span className="font-medium">{d.nome}</span> {d.sensivel ? <StatusBadge tone="warning">{t('context.sensitive')}</StatusBadge> : null}
                  </TableCell>
                  <TableCell className="font-mono text-[length:var(--lr-text-sm)]">{d.campo}</TableCell>
                  <TableCell>{t(`context.dimTypes.${d.tipo}`)}</TableCell>
                  <TableCell>{d.sinonimos.join(', ') || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </section>

        <section aria-labelledby={`${s.id}-queries`}>
          <h3 id={`${s.id}-queries`} className="mb-2 font-semibold">
            {t('context.queries')}
          </h3>
          <ul className="flex flex-col gap-2">
            {s.queries_validadas.map((q) => (
              <li key={q.id} className="rounded-[var(--lr-radius-md)] border border-border p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-[length:var(--lr-text-sm)]">{q.id}</span>
                  <Badge>{t('context.version', { v: q.versao })}</Badge>
                  {q.certificada_por ? (
                    <StatusBadge tone="success">
                      {t('context.certified')}: {q.certificada_por}
                    </StatusBadge>
                  ) : (
                    <StatusBadge tone="neutral">{t('context.notCertified')}</StatusBadge>
                  )}
                </div>
                <p className="mt-1">{q.exemplo}</p>
                <p className="mt-1 text-[length:var(--lr-text-sm)] text-fg-secondary">
                  {t('context.params')}:{' '}
                  {Object.entries(q.parametros)
                    .map(([n, p]) => `${n} (${dimName(p.dimensao)}, ${p.obrigatorio ? t('context.required') : t('context.optionalParam')})`)
                    .join('; ') || '-'}
                </p>
                {q.dimensoes.length ? (
                  <p className="text-[length:var(--lr-text-sm)] text-fg-secondary">{`${t('context.groupBy')}: ${q.dimensoes.map(dimName).join(', ')}`}</p>
                ) : null}
              </li>
            ))}
          </ul>
        </section>

        {s.roteiros.length ? (
          <section aria-labelledby={`${s.id}-roteiros`}>
            <h3 id={`${s.id}-roteiros`} className="mb-2 font-semibold">
              {t('context.playbooks')}
            </h3>
            {s.roteiros.map((r) => (
              <div key={r.id} className="mb-2">
                <p className="font-medium">{r.quando}</p>
                <ol className="list-decimal pl-5 text-fg-secondary">
                  {r.passos.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ol>
              </div>
            ))}
          </section>
        ) : null}
      </CardContent>
    </Card>
  );
}

export function PublishedView({ snapshot }: { snapshot: CatalogSnapshot | null }) {
  const { t } = useTranslation();
  if (!snapshot || snapshot.subjects.length === 0) return <EmptyState>{t('context.noCatalog')}</EmptyState>;
  return (
    <div className="flex flex-col gap-4">
      {snapshot.subjects.map((s) => (
        <SubjectCard key={s.id} s={s} />
      ))}
    </div>
  );
}

function RestoreDialog({ pub, onClose }: { pub: Publication; onClose: () => void }) {
  const { t } = useTranslation();
  const restore = useApiMutation('restorePublication', ['publications', 'catalog', 'overview']);
  const [reason, setReason] = useState('');
  function submit(e: FormEvent) {
    e.preventDefault();
    restore.mutate(
      { params: { id: pub.id }, body: { reason: reason.trim() } },
      {
        onSuccess: (p) => {
          toast.success(t('context.history.restored', { id: p.id }));
          onClose();
        },
        onError: (err) => toast.error(errorMessage(t, err)),
      },
    );
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={t('context.history.restoreTitle', { id: pub.id })} description={t('context.history.restoreHelp')} closeLabel={t('common.close')}>
        <form className="flex flex-col gap-4" onSubmit={submit}>
          <Field id="motivo-republicar" label={t('context.history.reason')}>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} required maxLength={500} />
          </Field>
          <div className="flex justify-end gap-2">
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" variant="primary" disabled={!reason.trim() || restore.isPending}>
              {t('context.history.restore')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function HistoryView({ publications, isAdmin }: { publications: Publication[]; isAdmin: boolean }) {
  const { t } = useTranslation();
  const [restoring, setRestoring] = useState<Publication | null>(null);
  const activeId = publications[0]?.id;
  return (
    <>
      <Table>
        <TableCaption>{t('context.tabHistory')}</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>{t('context.history.published')}</TableHead>
            <TableHead>{t('context.history.by')}</TableHead>
            <TableHead>{t('context.history.commit')}</TableHead>
            <TableHead>{t('context.history.reason')}</TableHead>
            <TableHead>{t('context.history.changes')}</TableHead>
            <TableHead>
              <span className="sr-only">{t('common.actions')}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {publications.map((p) => (
            <TableRow key={p.id}>
              <TableCell className="tabular whitespace-nowrap">
                {formatDateTime(p.created_at)}
                {p.id === activeId ? (
                  <span className="ml-2">
                    <StatusBadge tone="success">{t('context.history.active')}</StatusBadge>
                  </span>
                ) : null}
                <p className="font-mono text-[length:var(--lr-text-xs)] text-fg-muted">{p.id}</p>
              </TableCell>
              <TableCell>{p.created_by}</TableCell>
              <TableCell className="font-mono text-[length:var(--lr-text-sm)]">
                {p.commit ? p.commit.slice(0, 10) : '-'}
                {p.dirty ? <p className="text-[length:var(--lr-text-xs)] text-warning">{t('context.history.dirty')}</p> : null}
              </TableCell>
              <TableCell>
                {p.reason ?? '-'}
                {p.restored_from ? <p className="text-[length:var(--lr-text-xs)] text-fg-muted">{t('context.history.restoredFrom', { id: p.restored_from })}</p> : null}
              </TableCell>
              <TableCell>
                <ul className="list-disc pl-4 text-[length:var(--lr-text-sm)]">
                  {p.summary.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
              </TableCell>
              <TableCell className="text-right">
                {isAdmin && p.id !== activeId ? (
                  <Button size="sm" onClick={() => setRestoring(p)}>
                    {t('context.history.restore')}
                  </Button>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {restoring ? <RestoreDialog pub={restoring} onClose={() => setRestoring(null)} /> : null}
    </>
  );
}
